import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { Close, ExpandLess, ExpandMore, Timeline } from "@mui/icons-material";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import PeriodField from "components/_refactor/fields/period-field";
import ClearButton from "components/_refactor/button/clear";
import LogsTimeline from "./LogsTimeline";

const TIMELINE_SKELETON_ITEMS = 4;

function LogsTimelineSkeleton() {
  return (
    <Box
      style={{
        border: `1px solid ${themeColorsDesingSystem.stone.main}`,
        borderRadius: 8,
        padding: "1.5rem 2rem",
        marginTop: "1.5rem",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        style={{ marginBottom: "2rem" }}
      >
        <Skeleton variant="text" width={220} height={28} />
        <Skeleton variant="text" width={160} height={22} />
      </Box>

      {Array.from({ length: TIMELINE_SKELETON_ITEMS }).map((_, index) => (
        <Box
          key={index}
          display="flex"
          style={{ gap: 20, marginBottom: index < TIMELINE_SKELETON_ITEMS - 1 ? "2rem" : 0 }}
        >
          <Skeleton variant="circle" width={40} height={40} />
          <Box flex={1}>
            <Skeleton
              variant="rect"
              height={128}
              style={{ borderRadius: 8 }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

const useStyles = makeStyles({
  backdrop: {
    backgroundColor: "rgba(15, 23, 42, 0.6)",
    backdropFilter: "blur(4px)",
  },
  dialogPaper: {
    borderRadius: 8,
    maxWidth: 960,
    width: "100%",
    height: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    margin: 16,
  },
  header: {
    padding: "1.5rem 2rem 1rem",
    borderBottom: `1px solid ${themeColorsDesingSystem.stone.main}`,
    flexShrink: 0,
  },
  title: {
    fontSize: "1.375rem",
    fontWeight: 700,
    color: themeColorsDesingSystem.primary.main,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  tabs: {
    marginTop: "1rem",
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 700,
      fontSize: "1rem",
      minWidth: 80,
      padding: "0.75rem 0",
      marginRight: 32,
    },
    "& .MuiTabs-indicator": {
      height: 3,
      backgroundColor: themeColorsDesingSystem.primary.main,
    },
  },
  tabActive: {
    color: `${themeColorsDesingSystem.primary.main} !important`,
  },
  tabInactive: {
    color: `${themeColorsDesingSystem.gray.main} !important`,
  },
  body: {
    flex: 1,
    overflow: "auto",
    padding: "1.5rem",
    backgroundColor: themeColorsDesingSystem.background.main,
  },
  filtersBox: {
    border: `1px solid ${themeColorsDesingSystem.stone.main}`,
    borderRadius: 8,
    padding: "1.5rem",
    marginBottom: "1.5rem",
  },
  filtersHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    userSelect: "none",
  },
  filtersTitle: {
    fontSize: "1.125rem",
    fontWeight: 700,
    color: themeColorsDesingSystem.gray.dark,
  },
  searchButton: {
    backgroundColor: themeColorsDesingSystem.primary.main,
    color: themeColorsDesingSystem.background.main,
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 32px",
    "&:hover": {
      backgroundColor: themeColorsDesingSystem.primary.active,
    },
  },
});

export default function DetailHistoryModal({
  open,
  onClose,
  valueTab,
  onChangeTab,
  filterProps,
  timelineProps,
}) {
  const classes = useStyles();
  const [filtrosAbertos, setFiltrosAbertos] = useState(true);

  const {
    handleSearch,
    searching,
    disabled,
    handleClearFilters,
    loadingFilters,
    clearFilters,
    screensList,
    screen,
    setScreen,
    usersList,
    user,
    setUser,
    listSystem,
    selectedSystem,
    setSelectedSystem,
    period,
    setPeriod,
    initialDate,
    finalDate,
    setInitialDate,
    setFinalDate,
  } = filterProps;

  const { loading, logs = [] } = timelineProps;
  const showTimelineSkeleton = (searching || loading) && logs.length === 0;

  useEffect(() => {
    if (open) {
      setFiltrosAbertos(true);
    }
  }, [open]);

  const handleSearchClick = async () => {
    await handleSearch();
  };

  const handleClear = () => {
    handleClearFilters();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
      BackdropProps={{ className: classes.backdrop }}
    >
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography className={classes.title}>
            <Timeline />
            Pesquisa de Histórico Detalhado
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </Box>

      <Box className={classes.body}>
        <Box className={classes.filtersBox}>
          <Box
            className={classes.filtersHeader}
            onClick={() => setFiltrosAbertos(!filtrosAbertos)}
          >
            <Typography className={classes.filtersTitle}>Filtros</Typography>
            {filtrosAbertos ? (
              <ExpandLess
                style={{ color: themeColorsDesingSystem.gray.main }}
              />
            ) : (
              <ExpandMore
                style={{ color: themeColorsDesingSystem.gray.main }}
              />
            )}
          </Box>

          {filtrosAbertos && (
            <Box style={{ marginTop: "1.5rem" }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <AutoCompleteField
                    label="Tela"
                    loading={loadingFilters || clearFilters}
                    onChange={setScreen}
                    value={screen}
                    options={screensList}
                    disabled={loadingFilters || searching}
                    optionLabelKey="Tela"
                    optionIdKey="Sistema"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <AutoCompleteField
                    label="Usuário"
                    loading={loadingFilters || clearFilters}
                    onChange={setUser}
                    value={user}
                    options={usersList}
                    disabled={loadingFilters || searching}
                    optionIdKey="IdUsuario"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <AutoCompleteField
                    label="Sistema"
                    loading={loadingFilters || clearFilters}
                    onChange={setSelectedSystem}
                    value={selectedSystem}
                    options={listSystem}
                    disabled={loadingFilters || searching}
                    optionLabelKey="name"
                    optionIdKey="id"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3} style={{ marginTop: 8 }}>
                <Grid item xs={12}>
                  <PeriodField
                    label="Período"
                    value={period}
                    onChange={setPeriod}
                    disabled={searching}
                    initialDate={initialDate}
                    finalDate={finalDate}
                    setInitialDate={setInitialDate}
                    setFinalDate={setFinalDate}
                  />
                </Grid>
              </Grid>

              <Box
                display="flex"
                alignItems="center"
                style={{ gap: 16, marginTop: "1.5rem" }}
              >
                <Button
                  className={classes.searchButton}
                  variant="contained"
                  disableElevation
                  onClick={handleSearchClick}
                  disabled={disabled}
                >
                  {searching ? "Pesquisando..." : "Pesquisar"}
                </Button>
                <ClearButton disable={disabled} onClick={handleClear} />
              </Box>
            </Box>
          )}
        </Box>

        <Tabs
          value={valueTab}
          onChange={onChangeTab}
          className={classes.tabs}
          indicatorColor="primary"
        >
          <Tab
            label="Telas"
            value="1"
            className={
              valueTab === "1" ? classes.tabActive : classes.tabInactive
            }
          />
          <Tab
            label="Usuários"
            value="2"
            className={
              valueTab === "2" ? classes.tabActive : classes.tabInactive
            }
          />
        </Tabs>

        {showTimelineSkeleton ? (
          <LogsTimelineSkeleton />
        ) : (
          <Box style={{ marginTop: "1.5rem" }}>
            <LogsTimeline {...timelineProps} />
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
